import { Injectable } from '@angular/core';
import { StepThreeModel } from '../../../shared/models/states/loan/step-three.model';
import { BrowserStorageService, StorageKeys } from '../../services/browser-storage.service';
import { StepOneState } from './step-one-state';
import { ApplicantModel } from '../../../shared/models/states/applicant.model';
import { lastValueFrom } from 'rxjs';
import { JointApplicantService } from '../../services/loan/joint-applicant.api.service';
import { NonjointApplicantService } from '../../services/loan/nonjoint-applicant.api.service';

@Injectable({
    providedIn: 'root',
})
export class StepThreeState {
    private _state?: StepThreeModel;

    constructor(
        private bss: BrowserStorageService,
        private s1: StepOneState,
        private jointApplicantService: JointApplicantService,
        private nonjointApplicantService: NonjointApplicantService
    ) { }

    get state() {
        if (!this._state) {
            const item = this.bss.getSessionItem(StorageKeys.StepThreeModel);
            if (item) this._state = item;
            return item ?? undefined;
        }
        return this._state;
    }

    set state(value: StepThreeModel | undefined) {
        this.bss.setSessionItem(StorageKeys.StepThreeModel, value);
        this._state = value;
    }

    async syncDB() {
        if (this._state === undefined) return;
        for (let i = 0; i < this._state.applicants.length; i++) {
            const applicant = this._state.applicants[i];
            const doUpdate = applicant.uuid !== undefined;
            if (!doUpdate) await this.createNew(applicant, i);
            else await this.update(applicant);
        }
        this.bss.setSessionItem(StorageKeys.StepThreeModel, this._state);
    }

    deleteApplicant(idx: number) {
        const applicant = this._state?.applicants[idx];
        if (!applicant) return;
        const isJoint = applicant?.applicant_type === "Joint";
        if (applicant.uuid !== undefined && isJoint)
            this.jointApplicantService.deleteJointApplicant(applicant.uuid).subscribe(() => { }, (e) => console.error(e));
        else if (applicant.uuid !== undefined && !isJoint)
            this.nonjointApplicantService.deleteNonjointApplicant(applicant.uuid).subscribe(() => { }, (e) => console.error(e));
        this._state?.applicants.splice(idx, 1);
        this.bss.setSessionItem(StorageKeys.StepThreeModel, this._state);
    }

    private async createNew(value: ApplicantModel, i: number) {
        if (this.s1.uuid === undefined) return;
        const isJoint = value.applicant_type === "Joint";
        const res = isJoint ?
            await lastValueFrom(this.jointApplicantService.createJointApplicant(value, this.s1.uuid)) :
            await lastValueFrom(this.nonjointApplicantService.getOrCreateNonjointApplicant(value, this.s1.uuid))
        if (this._state) this._state.applicants[i].uuid = res.uuid;
    }

    private async update(value: ApplicantModel) {
        if (!value.uuid) return;
        const isJoint = value.applicant_type === "Joint";
        isJoint ?
            await lastValueFrom(this.jointApplicantService.updateJointApplicant(value.uuid, value)) :
            await lastValueFrom(this.nonjointApplicantService.updateNonjointApplicant(value.uuid, value))
    }
}
